import { StandardResponse } from '@/types/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getErrorMessage } from 'Src/utils/common';
import SettingsService from '../../../services/settings-service';

type AccessKeyData = {
  name: string;
  key?: string;
};
// Get all access keys
export const useGetAccessKeys = () => {
  return useQuery<StandardResponse<AccessKeyData>>({
    queryKey: ['accessKeys'],
    queryFn: async () => {
      const response = await SettingsService.getAccessKeys();
      return response as StandardResponse<AccessKeyData>;
    },
  });
};

// Get single access key by ID
export const useGetAccessKeyById = (accessKeyId: string | undefined) => {
  return useQuery<AccessKeyData>({
    queryKey: ['accessKey', accessKeyId],
    queryFn: async () => {
      const response = await SettingsService.getAccessKeysById(
        parseInt(accessKeyId!)
      );
      return response as AccessKeyData;
    },
    enabled: !!accessKeyId,
  });
};

interface UseCreateOrUpdateAccessKeyOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useCreateOrUpdateAccessKey = ({
  onSuccess,
  successMessage,
}: UseCreateOrUpdateAccessKeyOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id?: number;
      data: Partial<AccessKeyData>;
    }) => {
      if (id) {
        return SettingsService.updateAccessKeys(id, data);
      }
      return SettingsService.createAccessKeys(data);
    },
    onSuccess: (_, variables) => {
      const message =
        successMessage ??
        (variables.id
          ? 'Access key updated successfully'
          : 'Access key created successfully');
      toast.success(message, { position: 'top-right' });

      queryClient.invalidateQueries({ queryKey: ['accessKeys'] });
      if (variables.id) {
        queryClient.invalidateQueries({
          queryKey: ['accessKey', variables.id.toString()],
        });
      }
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage((error as any).response?.data ?? error));
    },
  });
};

interface UseDeleteAccessKeyOptions {
  onSuccess?: () => void;
  successMessage?: string;
}

export const useDeleteAccessKey = ({
  onSuccess,
  successMessage,
}: UseDeleteAccessKeyOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => {
      return SettingsService.deleteAccessKeys(id);
    },
    onSuccess: () => {
      toast.success(successMessage ?? 'Access key deleted successfully', {
        position: 'top-right',
      });
      queryClient.invalidateQueries({ queryKey: ['accessKeys'] });
      onSuccess?.();
    },
    onError: (error) => {
      toast.error(getErrorMessage((error as any).response?.data ?? error));
    },
  });
};

export default useDeleteAccessKey;
